import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { useFormik } from "formik";
import Stack from "@mui/material/Stack";
import { Button, Modal } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import assets from "../../assets";
import { useNavigate } from "react-router-dom";
import AxiosMain from "../../components/layout/AxiosMain";
import Swal from "sweetalert2";
import * as Yup from 'yup';
import LoaderScreen from "../../components/layout/LoaderScreen";
import { AppAPiUrl } from "../../configs/appData";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import TranslationGetHOC from "../../components/layout/TranslationGetHOC";
import PrivacyPolicyPage from "../PrivacyPolicy/PrivacyPolicyPage";

type Props = {
  translateDataft: any;
  translationStatus: any;
};

const ForgetPasswordPageComponent = (props: Props) => {
  // Styling for the Privacy Policy Modal
  const privacyPolicystyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: "100%",
    maxWidth: "900px;",
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding:"20px",
    maxHeight: "calc(100% - 20px)",
    overflow: "auto",
    borderRadius: "21px",
    border:"20px solid #fff",
    outline:"none !important"
  };

  // State Hooks
  const [openPrivacyModal, setPrivacyUserModal] = useState(false);
  const [iComponetTranslate, setICTranslation] = useState([]);
  const [isTransLoaded, setLoaded] = useState(false);
  const { appCountry } = useSelector((state: RootState) => state.appState);
  const navigate = useNavigate();

  // Translation Function
  const t = (name: string) => {
    const ifExist: any = iComponetTranslate.filter((item: any) => item.label === name);
    if (ifExist.length > 0) {
      return ifExist[0].value ? ifExist[0].value : `No translate found (${ifExist[0].label})`;
    }
    return `No key found (${name})`;
  }

  // Loading State
  const [isLoading, setLoading] = useState(false);

  // Form Steps: 1 for Email, 2 for OTP & Password
  const [step, setStep] = useState<1 | 2>(1);

  // Store the auth token after email submission
  const [authToken, setAuthToken] = useState<string | null>(null);

  // Validation Schemas
  const emailSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Required')
  });

  const otpPasswordSchema = Yup.object().shape({
    otp: Yup.string()
      .required('OTP is required')
      .matches(/^\d{6}$/, 'OTP must be a 4-digit number'),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters long'),
    confirm_password: Yup.string()
      .required('Confirm Password is required')
      .oneOf([Yup.ref('password')], 'Passwords must match'),
  });

  // SweetAlert Translations
  const formSwalTranslation = {
    formSwalTitle: t("SomethingWentWrong"),
    formSwalSucccesTitle: t("Checkyouremail"),
    formSwalInvalidOTP: t("InvalidOTP"),
    formSwalPasswordReset: t("PasswordResetSuccess"),
  };

  // Formik for Email Submission
  const formikEmail = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: emailSchema,
    onSubmit: (values) => {
      setLoading(true);
      AxiosMain.post(AppAPiUrl.Forgetpassword, values)
        .then((val) => {
          setLoading(false);
          formikEmail.resetForm();
          if (val.status === 200) {
            // Store the token received from the API
            const token = val.data.token;
            setAuthToken(token);
            if (val.data.otp !== undefined){
              console.log(val.data.otp);
            }
            // Transition to Step 2
            setStep(2);

            Swal.fire({
              text: val.data.message,
              icon: 'success',
              confirmButtonColor: '#103256',
              confirmButtonText: "OK",
              allowOutsideClick: false
            });
          }
        })
        .catch((e) => {
          setLoading(false);
          // Handle errors and stay on Step 1
          let msg = "An error occurred.";
          if (e.response && e.response.data) {
            if (e.response.data.email) {
              msg = e.response.data.email[0];
            } else if (e.response.data.message) {
              msg = e.response.data.message;
            }
          }
          Swal.fire({
            title: formSwalTranslation.formSwalTitle,
            text: msg,
            icon: 'warning',
          });
        });
    },
  });

  // Formik for OTP & Password Submission
  const formikOTPPassword = useFormik({
    initialValues: {
      otp: "",
      password: "",
      confirm_password: "",
    },
    validationSchema: otpPasswordSchema,
    onSubmit: (values) => {
      if (!authToken) {
        Swal.fire({
          title: formSwalTranslation.formSwalTitle,
          text: "Invalid session. Please try again.",
          icon: 'warning',
        });
        setStep(1);
        return;
      }

      setLoading(true);
      // Prepare the payload
      const payload = {
        token: authToken,
        otp: values.otp,
        password: values.password,
        confirm_password: values.confirm_password,
      };

      AxiosMain.post(AppAPiUrl.VerifyResetPassword, payload)
        .then((val) => {
          setLoading(false);
          formikOTPPassword.resetForm();
          if (val.status === 200) {
            Swal.fire({
              text: formSwalTranslation.formSwalPasswordReset,
              icon: 'success',
              confirmButtonColor: '#103256',
              confirmButtonText: "OK",
              allowOutsideClick: false
            }).then((result) => {
              navigate("/"); // Navigate to login page
            });
          }
        })
        .catch((e) => {
          setLoading(false);
          // Handle errors and switch back to Step 1
          let msg = "An error occurred.";
          if (e.response && e.response.data) {
            if (e.response.data.message) {
              msg = e.response.data.message;
            }
          }
          Swal.fire({
            title: formSwalTranslation.formSwalTitle,
            text: msg,
            icon: 'warning',
          }).then(() => {
            setStep(1); // Switch back to email form on failure
          });
        });
    },
  });

  // Prevent form submission on Enter key press outside of form fields
  const onKeyDown = (keyEvent: any, formCompo: any) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
    if (keyEvent.key === 'Enter') {
      formCompo.handleSubmit();
    }
  };

  // Load Translations
  useEffect(() => {
    const { translateDataft } = props;
    const newCode = appCountry.toLocaleLowerCase();
    if (translateDataft && translateDataft.hasOwnProperty(`${newCode}`)) {
      setICTranslation(translateDataft[`${newCode}`]);
      setTimeout(() => {
        setLoaded(true);
      }, 100);
    }
    else {
      setICTranslation([]);
      setLoaded(true);
    }
  }, [props, appCountry]);

  // Render Email Form
  const renderEmailForm = () => (
    <form onSubmit={formikEmail.handleSubmit} onKeyDown={(e) => onKeyDown(e, formikEmail)}>
      <Box sx={{ flexGrow: 1 }} className="form-mb-30">
        <Grid container spacing={2}>
          <Grid item xs={12} className="cm-form-grid-col">
            <Box sx={{ width: "100%" }}>
              <div className="form-label-name">{t("email")}</div>
              <FormControl sx={{ width: "100%" }}>
                <TextField
                  className="cm-login-field"
                  variant="outlined"
                  name="email"
                  value={formikEmail.values.email}
                  onChange={formikEmail.handleChange}
                  onBlur={formikEmail.handleBlur}
                  error={formikEmail.touched.email && Boolean(formikEmail.errors.email)}
                  helperText={formikEmail.touched.email && formikEmail.errors.email}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <MailOutlineIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ marginTop: "40px" }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent={"start"}
        >
          <Button
            type="submit"
            variant="contained"
            className="cm-login-submit-btn"
          >
            {t("submit")}
          </Button>
        </Stack>
      </Box>
      <Box
        sx={{ width: "100%", textAlign: "center", marginTop: "10px" }}
      >
        <Button
          variant="text"
          onClick={() => navigate("/")}
          className="cm-forgot-pass-btn"
        >
          {t("BackToLogin")}
        </Button>
      </Box>
    </form>
  );

  // Render OTP & Password Form
  const renderOTPPasswordForm = () => (
    <form onSubmit={formikOTPPassword.handleSubmit} onKeyDown={(e) => onKeyDown(e, formikOTPPassword)}>
      <Box sx={{ flexGrow: 1 }} className="form-mb-30">
        <Grid container spacing={2}>
          <Grid item xs={12} className="cm-form-grid-col">
            <Box sx={{ width: "100%" }}>
              <div className="form-label-name">{t("OTP")}</div>
              <FormControl sx={{ width: "100%" }}>
                <TextField
                  className="cm-login-field"
                  variant="outlined"
                  name="otp"
                  value={formikOTPPassword.values.otp}
                  onChange={formikOTPPassword.handleChange}
                  onBlur={formikOTPPassword.handleBlur}
                  error={formikOTPPassword.touched.otp && Boolean(formikOTPPassword.errors.otp)}
                  helperText={formikOTPPassword.touched.otp && formikOTPPassword.errors.otp}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {/* You can add an OTP icon here if available */}
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={12} className="cm-form-grid-col">
            <Box sx={{ width: "100%" }}>
              <div className="form-label-name">{t("NewPassword")}</div>
              <FormControl sx={{ width: "100%" }}>
                <TextField
                  className="cm-login-field"
                  variant="outlined"
                  type="password"
                  name="password"
                  value={formikOTPPassword.values.password}
                  onChange={formikOTPPassword.handleChange}
                  onBlur={formikOTPPassword.handleBlur}
                  error={formikOTPPassword.touched.password && Boolean(formikOTPPassword.errors.password)}
                  helperText={formikOTPPassword.touched.password && formikOTPPassword.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {/* You can add a lock icon here if available */}
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={12} className="cm-form-grid-col">
            <Box sx={{ width: "100%" }}>
              <div className="form-label-name">{t("ConfirmPassword")}</div>
              <FormControl sx={{ width: "100%" }}>
                <TextField
                  className="cm-login-field"
                  variant="outlined"
                  type="password"
                  name="confirm_password"
                  value={formikOTPPassword.values.confirm_password}
                  onChange={formikOTPPassword.handleChange}
                  onBlur={formikOTPPassword.handleBlur}
                  error={formikOTPPassword.touched.confirm_password && Boolean(formikOTPPassword.errors.confirm_password)}
                  helperText={formikOTPPassword.touched.confirm_password && formikOTPPassword.errors.confirm_password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {/* You can add a lock icon here if available */}
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ marginTop: "40px" }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent={"start"}
        >
          <Button
            type="submit"
            variant="contained"
            className="cm-login-submit-btn"
          >
            {t("submit")}
          </Button>
        </Stack>
      </Box>
      <Box
        sx={{ width: "100%", textAlign: "center", marginTop: "10px" }}
      >
        <Button
          variant="text"
          onClick={() => setStep(1)} // Allow user to go back to email form if needed
          className="cm-forgot-pass-btn"
        >
          {t("BackToEmail")}
        </Button>
      </Box>
    </form>
  );

  return (
    <section className="login-page-wrapper">
      <Modal
        open={openPrivacyModal}
        onClose={() => setPrivacyUserModal(false)}
        aria-labelledby="Modal-User-Edit"
        aria-describedby="Modal-User-Edit"
      >
        <Box sx={privacyPolicystyle}>
          <Box sx={{
            textAlign: "right",
            position: "absolute",
            top: "12px",
            right: "25px",
          }}>
            <CloseIcon onClick={() => setPrivacyUserModal(false)} sx={{
              color: "#CCCCCC",
              width: "28px",
              height: "28px",
              cursor: "pointer"
            }} />
          </Box>
          <Box>
            <PrivacyPolicyPage />
          </Box>
        </Box>
      </Modal>
      {
        isTransLoaded ? (
          <Box sx={{ flexGrow: 1 }}>
            <Grid
              container
              direction="row"
              justifyContent="center"
              className="cm-login-main-row"
              alignItems={"center"}
            >
              <Grid
                item
                lg={7}
                md={6}
                xs={12}
                style={{
                  background: `linear-gradient(0deg, rgba(0, 24, 32, 0.81), rgba(0, 24, 32, 0.81)), url(${assets.images.loginBGImage})`,
                }}
                className="cm-col-login-bg cm-login-main-col"
              >
                <Grid
                  container
                  spacing={2}
                  direction="row"
                  justifyContent="center"
                  className="cm-login-row"
                  alignItems="center"
                >
                  <Grid item xs={12}>
                    <div className="login-innter-content">
                      <h2>{t("login_leftfColTitle")}</h2>
                      <p>{t("login_leftColDescription")}</p>
                      <Button
                        variant="contained"
                        startIcon={
                          <Box
                            sx={{
                              flexGrow: 1,
                              background: "#fff",
                              borderRadius: "100%",
                              height: "30px",
                            }}
                          >
                            <PlayCircleFilledWhiteIcon sx={{ color: "#007992" }} />
                          </Box>
                        }
                        className="cm-login-btn-style"
                      >
                        {t("login_ButtonWatchText")}
                      </Button>
                    </div>
                  </Grid>
                </Grid>
                <p className="login-copyright">
                  © 2021 - Setera | <span style={{ cursor: "pointer" }} onClick={() => setPrivacyUserModal(true)}>{t("Privacy")} {t("Policy")}</span>
                </p>
              </Grid>
              <Grid item lg={5} md={6} xs={12} className="cm-login-main-col">
                <div className="cm-login-form-wrap">
                  <h2>
                    {step === 1 ? t("forgotPass_FormTitle") : t("resetPass_FormTitle")}
                  </h2>
                  {
                    isLoading ? (<LoaderScreen />) : (
                      step === 1 ? renderEmailForm() : renderOTPPasswordForm()
                    )
                  }
                </div>
              </Grid>
            </Grid>
          </Box>
        ) : <LoaderScreen msg='Loading Data' />
      }
    </section>
  );
};

function ForgetPasswordPage() {
  const dependency = {};
  const MainComponent = TranslationGetHOC(ForgetPasswordPageComponent, "unknown_User", true, {}, dependency);
  return <MainComponent />
}

export default ForgetPasswordPage;